// src/components/customers/CustomerTable.jsx
import React from "react";
import IconUser from "../../components/icons/IconUser";
import IconEdit from "../../components/icons/IconEdit";
import IconTrash from "../../components/icons/IconTrash";
import IconTrendingUp from "../../components//icons/IconTrendingUp";

/**
 * Enhanced customer table component with light theme and improved UI/UX
 * Displays customer data in a responsive table with action buttons
 *
 * @param {Object} props - Component props
 * @param {Array} props.customers - Array of customer objects to display
 * @param {boolean} props.loading - Whether data is currently loading
 * @param {Object} props.pagination - Pagination configuration and handlers
 * @param {Function} props.onEdit - Function to handle customer editing
 * @param {Function} props.onDelete - Function to handle customer deletion
 * @param {Function} props.onViewJobs - Function to handle viewing customer jobs
 * @returns {JSX.Element} Rendered component
 */
const CustomerTable = ({
  customers,
  loading,
  pagination,
  onEdit,
  onDelete,
  onViewJobs,
}) => {
  // Show loading state with animated spinner
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-8 flex justify-center">
        <div className="flex flex-col items-center">
          {/* Animated loading spinner */}
          <div className="w-12 h-12 border-4 border-t-4 border-gray-200 border-t-blue-500 rounded-full animate-spin"></div>
          <p className="mt-4 text-gray-600">Loading customers...</p>
        </div>
      </div>
    );
  }

  // Show empty state when no customers are found
  if (!customers || customers.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-8 text-center">
        <div className="flex flex-col items-center">
          <IconUser className="w-16 h-16 text-gray-300" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">
            No Customers Found
          </h3>
          <p className="mt-2 text-gray-500">
            Try adjusting your search or add a new customer to get started.
          </p>
          {/* Add a helpful action button for empty state */}
          <button
            onClick={() => onEdit()}
            className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
            aria-label="Add new customer"
          >
            Add Customer
          </button>
        </div>
      </div>
    );
  }

  // Table container - only render when we have customers
  return (
    <div className="bg-white rounded-lg shadow-sm overflow-hidden transition-all duration-200">
      {/* Mobile Card View - Hidden on desktop */}
      <div className="md:hidden divide-y divide-gray-200">
        {customers.map((customer) => (
          <div
            key={customer.id}
            className="p-4 hover:bg-gray-50 transition-colors"
          >
            {/* Customer header with avatar and name */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center flex-1 min-w-0">
                <div className="flex-shrink-0 h-12 w-12 bg-indigo-100 rounded-full flex items-center justify-center">
                  <IconUser className="h-6 w-6 text-indigo-600" />
                </div>
                <div className="ml-3 flex-1 min-w-0">
                  <p className="text-base font-semibold text-gray-900 truncate">
                    {customer.name || "—"}
                  </p>
                  <p className="text-sm text-gray-500 truncate">
                    {customer.email || "No email"}
                  </p>
                </div>
              </div>
            </div>

            {/* Customer details */}
            <div className="space-y-2 mb-3">
              {customer.location && (
                <div className="flex items-start">
                  <svg
                    className="w-4 h-4 text-gray-400 mt-0.5 mr-2 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  <span className="text-sm text-gray-600">
                    {customer.location}
                  </span>
                </div>
              )}

              {customer.phone && (
                <div className="flex items-center">
                  <svg
                    className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                    />
                  </svg>
                  <span className="text-sm text-gray-600">
                    {customer.phone}
                  </span>
                </div>
              )}

              {customer.postCode && (
                <div className="flex items-center">
                  <svg
                    className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                  <span className="text-sm text-gray-600">
                    {customer.postCode}
                  </span>
                </div>
              )}
            </div>

            {/* Action buttons - larger for mobile */}
            <div className="flex gap-2 pt-2 border-t border-gray-100">
              <button
                onClick={() => onViewJobs(customer.id)}
                className="flex-1 inline-flex items-center justify-center px-4 py-2.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors"
              >
                <IconTrendingUp className="h-4 w-4 mr-1.5" />
                View Jobs
              </button>
              <button
                onClick={() => onEdit(customer)}
                className="inline-flex items-center justify-center px-4 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                aria-label={`Edit ${customer.name}`}
              >
                <IconEdit className="h-4 w-4" />
              </button>
              <button
                onClick={() => onDelete(customer)}
                className="inline-flex items-center justify-center px-4 py-2.5 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                aria-label={`Delete ${customer.name}`}
              >
                <IconTrash className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Desktop Table View - Hidden on mobile */}
      <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {/* Table headers - using uppercase tracking for better readability */}
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Customer
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Email
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Location
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Phone
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Post Code
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {/* Map through customer data to create table rows */}
            {customers.map((customer) => (
              <tr
                key={customer.id}
                className="hover:bg-gray-50 transition-colors"
              >
                {/* Customer name cell with avatar */}
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    {/* Customer avatar/icon */}
                    <div className="flex-shrink-0 h-10 w-10 bg-indigo-100 rounded-full flex items-center justify-center">
                      <IconUser className="h-6 w-6 text-indigo-600" />
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">
                        {customer.name || "—"}
                      </div>
                    </div>
                  </div>
                </td>

                {/* Email cell - with fallback for missing data */}
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {customer.email || "—"}
                </td>

                {/* Location cell - with truncation for long text */}
                <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                  <div
                    title={customer.location}
                    className="truncate max-w-[200px]"
                  >
                    {customer.location || "—"}
                  </div>
                </td>

                {/* Phone number cell */}
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {customer.phone || "—"}
                </td>

                {/* Post code cell */}
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {customer.postCode || "—"}
                </td>

                {/* Actions cell with edit, delete and view jobs buttons */}
                <td className="px-6 py-4 whitespace-nowrap text-center">
                  <div className="flex justify-center space-x-2">
                    {/* Edit button with tooltip and accessibility attributes */}
                    <button
                      onClick={() => onEdit(customer)}
                      className="text-indigo-600 hover:text-indigo-900 transition-colors p-2 rounded-full hover:bg-indigo-50"
                      aria-label={`Edit ${customer.name}`}
                      title="Edit customer"
                    >
                      <IconEdit className="h-5 w-5" />
                      <span className="sr-only">Edit {customer.name}</span>
                    </button>

                    {/* Delete button with tooltip and accessibility attributes */}
                    <button
                      onClick={() => onDelete(customer)}
                      className="text-red-600 hover:text-red-900 transition-colors p-2 rounded-full hover:bg-red-50"
                      aria-label={`Delete ${customer.name}`}
                      title="Delete customer"
                    >
                      <IconTrash className="h-5 w-5" />
                      <span className="sr-only">Delete {customer.name}</span>
                    </button>

                    {/* View jobs button with tooltip and accessibility attributes */}
                    <button
                      onClick={() => {
                        onViewJobs(customer.id);
                      }}
                      className="text-green-600 hover:text-green-900 transition-colors p-2 rounded-full hover:bg-green-50"
                      aria-label={`View jobs for ${customer.name}`}
                      title="View jobs"
                    >
                      <IconTrendingUp className="h-5 w-5" />
                      <span className="sr-only">
                        View jobs for {customer.name}
                      </span>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination controls - only show if we have multiple pages */}
      {pagination && pagination.totalPages > 1 && (
        <div className="px-4 md:px-6 py-3 flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-gray-200">
          {/* Page indicator - shown on top for mobile */}
          <div className="text-sm text-gray-700 font-medium order-first sm:order-none">
            Page {pagination.currentPage + 1} of {pagination.totalPages}
          </div>

          {/* Navigation buttons */}
          <div className="flex gap-2 w-full sm:w-auto">
            {/* Previous page button */}
            <button
              onClick={pagination.onPrevPage}
              disabled={pagination.currentPage === 0}
              className={`flex-1 sm:flex-none inline-flex items-center justify-center px-4 py-2.5 border border-gray-300 text-sm font-medium rounded-lg transition-colors ${
                pagination.currentPage === 0
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white text-gray-700 hover:bg-gray-50 active:bg-gray-100"
              }`}
              aria-label="Previous page"
            >
              <svg
                className="w-4 h-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              <span className="hidden sm:inline">Previous</span>
              <span className="sm:hidden">Prev</span>
            </button>

            {/* Next page button */}
            <button
              onClick={pagination.onNextPage}
              disabled={pagination.currentPage >= pagination.totalPages - 1}
              className={`flex-1 sm:flex-none inline-flex items-center justify-center px-4 py-2.5 border border-gray-300 text-sm font-medium rounded-lg transition-colors ${
                pagination.currentPage >= pagination.totalPages - 1
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white text-gray-700 hover:bg-gray-50 active:bg-gray-100"
              }`}
              aria-label="Next page"
            >
              <span className="hidden sm:inline">Next</span>
              <span className="sm:hidden">Next</span>
              <svg
                className="w-4 h-4 ml-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerTable;
